import i18next from 'i18next';
import { DateFormatter } from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';
import CopyableText from 'components/atoms/buttons/copyable-text';
import CopyIcon from 'components/atoms/icons/copyIcon';
import { tableSortMap } from 'infrastructure/antd/functions/create-sort-order';
import { useAppDispatch } from 'store';
import { Link } from 'react-router-dom';
import { Routes } from 'router/routes';

import s from './styles.module.scss';
import NoteCell from '../note-cell';
import DueDateCell from '../due-date-cell';

import type {
  IPartialsTableData,
  IPartialsTableDataFilters,
  IKeyNoteData,
} from 'infrastructure/interfaces';
import type { IBaseTableColumns } from 'components/atoms/base-table/types';

interface Props {
  filter: IPartialsTableDataFilters;
}

export const useColumns = ({
  filter,
}: Props): IBaseTableColumns<IPartialsTableData> => {
  const {
    ccmConsole: { setTableNote, toggleOpenDrawer },
  } = useAppDispatch();

  return [
    {
      label: i18next.t('labels.name'),
      key: 'name',
      width: '215px',
      dataCy: 'partials-patients-name',
      sortable: true,
      sortKey: 'name',
      sortOrder: filter['p-name'] ? tableSortMap[filter['p-name']] : undefined,
      render(name: string, record) {
        return (
          <Link to={Routes.patientGuid(record.guid)} className={s.link}>
            {name}
          </Link>
        );
      },
    },
    {
      label: i18next.t('labels.ptID'),
      key: 'guid',
      width: '75px',
      align: 'center',
      dataCy: 'partials-patients-guid',
      render(guid: string) {
        return (
          <CopyableText
            wrapperClassName={s.copyable}
            value={guid}
            label="Copy"
            icon={<CopyIcon />}
          />
        );
      },
    },
    {
      label: i18next.t('labels.provider'),
      key: 'provider',
      width: '190px',
      align: 'left',
      dataCy: 'partials-patients-provider',
      sortable: true,
      sortOrder: filter['p-provider']
        ? tableSortMap[filter['p-provider']]
        : undefined,
      render(provider: string) {
        return provider;
      },
    },
    {
      label: i18next.t('labels.callsMonth'),
      key: 'callsMonth',
      width: '105px',
      align: 'center',
      dataCy: 'partials-patients-calls-month',
      sortable: true,
      sortOrder: filter['p-callsMonth']
        ? tableSortMap[filter['p-callsMonth']]
        : undefined,
    },
    {
      label: i18next.t('labels.dueDate'),
      key: 'dueDate',
      width: '120px',
      align: 'center',
      dataCy: 'partials-patients-due-date',
      sortable: true,
      sortOrder: filter['p-dueDate']
        ? tableSortMap[filter['p-dueDate']]
        : undefined,
      render(dueDate: string | null, record) {
        return <DueDateCell date={dueDate} patientGuid={record.guid} />;
      },
    },
    {
      label: i18next.t('labels.ccmAppt'),
      key: 'appointmentDate',
      width: '90px',
      align: 'center',
      dataCy: 'partials-patients-appointment-date',
      sortable: true,
      sortOrder: filter['p-appointmentDate']
        ? tableSortMap[filter['p-appointmentDate']]
        : undefined,
      render(appointmentDate: string) {
        return DateFormatter({
          date: appointmentDate,
          format: DateFormats['MMM d, yyyy'],
        });
      },
    },
    {
      label: i18next.t('labels.type'),
      key: 'type',
      width: '80px',
      align: 'center',
      dataCy: 'partials-patients-type',
      sortable: false,
      sortOrder: filter['p-type'] ? tableSortMap[filter['p-type']] : undefined,
    },
    {
      label: i18next.t('labels.minutes'),
      key: 'minutes',
      width: '75px',
      align: 'center',
      dataCy: 'partials-patients-minutes',
      sortable: true,
      sortOrder: filter['p-minutes']
        ? tableSortMap[filter['p-minutes']]
        : undefined,
    },
    {
      label: i18next.t('labels.nextBillable'),
      key: 'nextBillable',
      width: '75px',
      align: 'center',
      dataCy: 'partials-patients-next-billable',
      sortable: true,
      sortOrder: filter['p-nextBillable']
        ? tableSortMap[filter['p-nextBillable']]
        : undefined,
    },
    {
      label: i18next.t('labels.lastCall'),
      key: 'lastCallDate',
      width: '95px',
      align: 'center',
      dataCy: 'partials-patients-last-call',
      sortable: false,
      sortOrder: filter['p-lastCallDate']
        ? tableSortMap[filter['p-lastCallDate']]
        : undefined,
      render(lastCallDate: string) {
        return DateFormatter({
          date: lastCallDate,
          format: DateFormats['MMM d, yyyy'],
        });
      },
    },
    {
      label: i18next.t('labels.notes'),
      width: '200px',
      key: 'notes',
      render(notes: IKeyNoteData[], record) {
        const note = notes.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        )[0];
        return (
          <NoteCell
            text={note?.note ?? ''}
            onClick={() => {
              setTableNote({
                notes,
                tableName: 'partials',
                patientGuid: record.guid,
                patientName: record.name,
              });
              toggleOpenDrawer();
            }}
          />
        );
      },
      tableCollClassName: s['note-cell'],
      dataCy: 'partials-patients-notes',
    },
  ];
};
